import type {
  Answers,
  DeclinePattern,
  EvidenceItem,
  ExpectedOutcome,
  QolImpact,
  RootCause,
  SuppressionLevel,
  TSuppressionProfile,
} from "./_schema";

/**
 * Derives a T-suppression profile from Ignite quiz answers.
 *
 * Pure function of `answers`. Same inputs always produce the same output, so
 * the profile is reproducible server-side from the persisted submission.
 *
 * Mapping rules are sourced from `docs/t-suppression-research.md`:
 * - Tier-based severity scoring (Section 1)
 * - Decline-pattern selection (Section 2)
 * - Root-cause inference (Sections 3 and 5)
 * - QoL impact dimension count (Section 4)
 *
 * This is a marketing-quiz heuristic, not a clinical diagnosis. Copy generated
 * from the output must frame results as a profile or pattern, not a diagnosis.
 */

type IgniteAnswer<K extends string> = K extends keyof IgniteAnswers ? IgniteAnswers[K] : undefined;

type IgniteAnswers = {
  age: "50-plus" | "40-49" | "30-39" | "20-29";
  concern: "energy" | "muscle" | "sex-drive" | "focus" | "weight-loss";
  "stress-symptoms": "yes" | "no";
  "energy-3pm": "drained" | "afternoon-slump" | "slightly-tired" | "same-energy";
  "when-started": "over-year" | "past-year" | "few-months" | "not-sure";
  "results-want": "energy" | "muscle" | "sex-drive" | "focus" | "all-of-above";
  "body-composition": "lean-muscular" | "average" | "soft-some-muscle" | "overweight";
  "sleep-hours": "lt-5" | "5-6" | "7-8" | "gt-8";
  "family-history": "yes" | "no" | "not-sure";
  "processed-foods": "daily" | "several-week" | "occasionally" | "rarely";
};

function read<K extends keyof IgniteAnswers>(answers: Answers, key: K): IgniteAnswer<K> | undefined {
  const v = answers[key];
  if (typeof v !== "string") return undefined;
  return v as IgniteAnswer<K>;
}

// -- Severity scoring -----------------------------------------------------

type Signals = {
  tier1: number;
  tier2: number;
  tier3: number;
};

/**
 * Extra severity signals contributed by a wrapping deriver (quiz-v7's cohort
 * gradient questions). Added to the counts from `countSignals` before severity
 * is computed. Severity-only by design: evidence chips, QoL, and root causes
 * still derive from the real `answers`, never from these signals — so a wrapper
 * can raise severity without manufacturing chips the user never answered.
 */
export type ExtraSignals = { tier1?: number; tier2?: number; tier3?: number };

function countSignals(answers: Answers): Signals {
  const concern = read(answers, "concern");
  const energy3pm = read(answers, "energy-3pm");
  const stress = read(answers, "stress-symptoms");
  const body = read(answers, "body-composition");
  const sleep = read(answers, "sleep-hours");
  const age = read(answers, "age");
  const family = read(answers, "family-history");
  const processed = read(answers, "processed-foods");
  const whenStarted = read(answers, "when-started");

  // Tier 1: highest correlation with measured low T (libido, severe fatigue,
  // depressed mood / loss of vigor). The Ignite quiz lacks a direct libido
  // question (research doc flags this gap), so concern=sex-drive is used as a
  // self-reported libido proxy.
  let tier1 = 0;
  if (concern === "sex-drive") tier1 += 1;
  if (energy3pm === "drained") tier1 += 1;
  if (stress === "yes" && (energy3pm === "drained" || energy3pm === "afternoon-slump")) tier1 += 1;

  // Tier 2: meaningful but lower-correlation symptoms (muscle loss, belly fat,
  // brain fog, irritability, sleep disturbance proxy).
  let tier2 = 0;
  if (body === "soft-some-muscle" || body === "overweight") tier2 += 1;
  if (concern === "muscle") tier2 += 1;
  if (concern === "focus") tier2 += 1;
  if (stress === "yes") tier2 += 1;
  if (energy3pm === "afternoon-slump") tier2 += 1;

  // Tier 3: lifestyle multipliers and demographic risk factors.
  let tier3 = 0;
  if (sleep === "lt-5" || sleep === "5-6") tier3 += 1;
  if (age === "50-plus" || age === "40-49") tier3 += 1;
  if (family === "yes") tier3 += 1;
  if (processed === "daily") tier3 += 1;
  if (body === "overweight") tier3 += 1;
  if (whenStarted === "over-year") tier3 += 1;

  return { tier1, tier2, tier3 };
}

function baseSeverity(signals: Signals): SuppressionLevel {
  const { tier1, tier2, tier3 } = signals;
  if (tier1 >= 3) return "high";
  if (tier1 >= 2 && tier2 >= 2 && tier3 >= 2) return "high";
  if (tier1 >= 2) return "medium";
  if (tier1 >= 1 && tier2 >= 2 && tier3 >= 2) return "medium";
  // Floor at NORMAL ("EARLY DECLINE"): anyone who completed a symptom-motivated
  // quiz is feeling a consequence, so the result page never absolves them with
  // a STABLE verdict. "low" is unreachable from the deriver (kept in the schema
  // enum as a defensive fallback only).
  return "normal";
}

// Index 0 ("low") is intentionally unreachable: baseSeverity floors at "normal"
// and every bumpLevel call is +1, so nothing lands on it. Kept as the array's
// floor so a future negative bump can't index out of range. The floor-lock test
// in tests/quizzes/suppression-floor.test.ts guards against "low" resurfacing.
const LEVELS: SuppressionLevel[] = ["low", "normal", "medium", "high"];

function bumpLevel(level: SuppressionLevel, by: number): SuppressionLevel {
  const idx = LEVELS.indexOf(level);
  const next = Math.max(0, Math.min(LEVELS.length - 1, idx + by));
  return LEVELS[next]!;
}

function applyTieBreakers(level: SuppressionLevel, answers: Answers): SuppressionLevel {
  let out = level;
  const sleep = read(answers, "sleep-hours");
  const stress = read(answers, "stress-symptoms");
  const energy3pm = read(answers, "energy-3pm");
  const age = read(answers, "age");
  const whenStarted = read(answers, "when-started");

  if (sleep === "lt-5") out = bumpLevel(out, 1);
  if (stress === "yes" && energy3pm === "drained") out = bumpLevel(out, 1);
  if (age === "50-plus" && whenStarted === "over-year") out = bumpLevel(out, 1);

  // No LOW floor: the old optimization-profile branch (lean / slept well / no
  // major concern -> "low") was removed so the result never reads STABLE. The
  // optimization profile now floors at NORMAL like everyone else.
  return out;
}

function deriveSuppressionLevel(answers: Answers, extra?: ExtraSignals): SuppressionLevel {
  const signals = countSignals(answers);
  const combined: Signals = {
    tier1: signals.tier1 + (extra?.tier1 ?? 0),
    tier2: signals.tier2 + (extra?.tier2 ?? 0),
    tier3: signals.tier3 + (extra?.tier3 ?? 0),
  };
  return applyTieBreakers(baseSeverity(combined), answers);
}

// -- Decline pattern ------------------------------------------------------

function deriveDeclinePattern(answers: Answers): DeclinePattern {
  const whenStarted = read(answers, "when-started");
  const age = read(answers, "age");
  const body = read(answers, "body-composition");
  const sleep = read(answers, "sleep-hours");
  const stress = read(answers, "stress-symptoms");
  const energy3pm = read(answers, "energy-3pm");

  // The lean / slept-well / no-stress profile previously returned "stable"
  // (rendered as the STABLE grid card). It now floors to "early-onset" —
  // subtle/diffuse onset — so the decline pattern never reads STABLE either,
  // and can't contradict a DECLINING/SUPPRESSED assessment. "stable" is
  // unreachable from the deriver (kept in the schema enum as a defensive value).
  const optimization =
    body === "lean-muscular" &&
    (sleep === "7-8" || sleep === "gt-8") &&
    stress !== "yes" &&
    energy3pm !== "drained";
  if (optimization) return "early-onset";

  if (whenStarted === "over-year") {
    if (age === "50-plus" || age === "40-49") return "progressive";
    return "stalled-recovery";
  }

  if (whenStarted === "past-year" || whenStarted === "few-months") {
    return "acute";
  }

  // "not-sure" or missing -- subtle/diffuse onset, common in skeptical buyers.
  return "early-onset";
}

// -- Root causes (top 2 by score) ----------------------------------------

const ROOT_CAUSE_ORDER: RootCause[] = [
  "cortisol-dominance",
  "chronic-stress",
  "sleep-deprivation",
  "visceral-adiposity",
  "metabolic-syndrome",
  "age-related-decline",
  "overtraining",
  "lifestyle-load",
  "endocrine-disruption",
  // Forced-primary-only (quiz-before-trt). Never scored, so it is filtered out of
  // organic picks; listed here only to keep the order array exhaustive.
  "blocked-testosterone",
];

function scoreRootCauses(answers: Answers): Map<RootCause, number> {
  const concern = read(answers, "concern");
  const energy3pm = read(answers, "energy-3pm");
  const stress = read(answers, "stress-symptoms");
  const body = read(answers, "body-composition");
  const sleep = read(answers, "sleep-hours");
  const age = read(answers, "age");
  const family = read(answers, "family-history");
  const processed = read(answers, "processed-foods");
  const whenStarted = read(answers, "when-started");

  const scores = new Map<RootCause, number>();
  const add = (k: RootCause, n: number) => scores.set(k, (scores.get(k) ?? 0) + n);

  // Cortisol dominance: stress + collapsed afternoon energy.
  if (stress === "yes" && energy3pm === "drained") add("cortisol-dominance", 3);
  else if (stress === "yes" && energy3pm === "afternoon-slump") add("cortisol-dominance", 2);
  else if (stress === "yes") add("cortisol-dominance", 1);

  // Chronic stress (the lifestyle, complementary to the hormonal mechanism).
  if (stress === "yes" && (whenStarted === "past-year" || whenStarted === "few-months"))
    add("chronic-stress", 3);
  else if (stress === "yes") add("chronic-stress", 2);

  // Sleep deprivation.
  if (sleep === "lt-5") add("sleep-deprivation", 3);
  else if (sleep === "5-6") add("sleep-deprivation", 2);

  // Visceral adiposity / aromatase load.
  if (body === "overweight") add("visceral-adiposity", 3);
  else if (body === "soft-some-muscle") add("visceral-adiposity", 2);
  if (concern === "weight-loss") add("visceral-adiposity", 1);

  // Metabolic syndrome cluster.
  if (family === "yes" && (body === "overweight" || body === "soft-some-muscle"))
    add("metabolic-syndrome", 3);
  else if (family === "yes") add("metabolic-syndrome", 1);
  if (processed === "daily" && (body === "overweight" || body === "soft-some-muscle"))
    add("metabolic-syndrome", 1);

  // Age-related decline.
  if (age === "50-plus" && whenStarted === "over-year") add("age-related-decline", 3);
  else if (age === "50-plus") add("age-related-decline", 2);
  else if (age === "40-49" && whenStarted === "over-year") add("age-related-decline", 2);

  // Overtraining (performance pattern).
  if (
    body === "lean-muscular" &&
    (concern === "muscle" || concern === "focus") &&
    (energy3pm === "drained" || energy3pm === "afternoon-slump")
  ) {
    add("overtraining", 3);
  }

  // Lifestyle load (multifactorial catch-all).
  let lifestyle = 0;
  if (processed === "daily") lifestyle += 1;
  if (sleep === "lt-5" || sleep === "5-6") lifestyle += 1;
  if (stress === "yes") lifestyle += 1;
  if (lifestyle >= 2) add("lifestyle-load", lifestyle);

  // Endocrine disruptors (background load).
  if (processed === "daily") add("endocrine-disruption", 1);

  return scores;
}

// When scoring yields fewer than 2 causes (the optimization profile),
// backfill with background-load explanations rather than acute pathology.
// Order is the priority an optimization-profile user should see; deduped
// against scored picks. (Severity for this profile still floors at NORMAL.)
const ROOT_CAUSE_BACKFILL: RootCause[] = [
  "lifestyle-load",
  "endocrine-disruption",
  "age-related-decline",
];

function pickRootCauses(answers: Answers): RootCause[] {
  const scores = scoreRootCauses(answers);
  const ranked = ROOT_CAUSE_ORDER.filter((c) => (scores.get(c) ?? 0) > 0).sort((a, b) => {
    const sa = scores.get(a) ?? 0;
    const sb = scores.get(b) ?? 0;
    if (sa !== sb) return sb - sa;
    return ROOT_CAUSE_ORDER.indexOf(a) - ROOT_CAUSE_ORDER.indexOf(b);
  });

  const picked: RootCause[] = ranked.slice(0, 2);
  for (const candidate of ROOT_CAUSE_BACKFILL) {
    if (picked.length >= 2) break;
    if (picked.includes(candidate)) continue;
    picked.push(candidate);
  }
  return picked;
}

// -- Quality of life impact ----------------------------------------------

function deriveQolImpact(answers: Answers): QolImpact {
  const concern = read(answers, "concern");
  const energy3pm = read(answers, "energy-3pm");
  const stress = read(answers, "stress-symptoms");
  const body = read(answers, "body-composition");
  const sleep = read(answers, "sleep-hours");

  // Tier-1 QoL domains: energy, libido, mood. Heaviest weight in the count.
  const tier1Domains: boolean[] = [
    concern === "energy" || energy3pm === "drained" || energy3pm === "afternoon-slump",
    concern === "sex-drive",
    stress === "yes",
  ];
  // Tier-2 domains: body composition, cognition, sleep.
  const tier2Domains: boolean[] = [
    concern === "muscle" ||
      concern === "weight-loss" ||
      body === "soft-some-muscle" ||
      body === "overweight",
    concern === "focus",
    sleep === "lt-5" || sleep === "5-6",
  ];

  const tier1Hits = tier1Domains.filter(Boolean).length;
  const tier2Hits = tier2Domains.filter(Boolean).length;
  const total = tier1Hits + tier2Hits;

  if (tier1Hits >= 2 || total >= 4) return "high";
  if (tier1Hits >= 1 && total >= 2) return "medium";
  if (total >= 2) return "medium";
  return "low";
}

// -- Assessment copy bank -------------------------------------------------

const ASSESSMENT_LABEL: Record<SuppressionLevel, string> = {
  low: "STABLE",
  normal: "EARLY DECLINE",
  medium: "DECLINING",
  high: "SUPPRESSED",
};

const ASSESSMENT_HEADLINE: Record<SuppressionLevel, string> = {
  low: "Your Testosterone Assessment: STABLE",
  normal: "Your Testosterone Assessment: EARLY DECLINE",
  medium: "Your Testosterone Assessment: DECLINING",
  high: "Your Testosterone Assessment: SUPPRESSED",
};

const EXPLANATION: Record<RootCause, Record<"stronger" | "softer", string>> = {
  "cortisol-dominance": {
    stronger:
      "Based on your responses, your testosterone production is being actively suppressed by chronic stress hormones.",
    softer:
      "Your responses show stress hormones starting to interfere with testosterone production.",
  },
  "chronic-stress": {
    stronger:
      "Sustained stress is overriding your hormonal recovery cycle. Your body is in defense mode, not rebuild mode.",
    softer: "Stress patterns are starting to shift your hormonal balance away from testosterone.",
  },
  "sleep-deprivation": {
    stronger:
      "Your sleep window is too short for the testosterone production cycle to complete. Most T release happens during deep sleep -- you are missing it.",
    softer: "Your sleep window is short enough to reduce nightly testosterone production.",
  },
  "visceral-adiposity": {
    stronger:
      "Body composition is converting your testosterone into estrogen via the aromatase pathway, creating a self-reinforcing loop.",
    softer:
      "Adipose tissue is starting to convert testosterone into estrogen, slowing your hormonal recovery.",
  },
  "metabolic-syndrome": {
    stronger:
      "Insulin and inflammatory signals are suppressing Leydig-cell testosterone production directly.",
    softer: "Metabolic markers are pointing toward early hormonal disruption.",
  },
  "lifestyle-load": {
    stronger:
      "Multiple lifestyle pressures are stacking on each other. There is no single fix -- your system needs a reset.",
    softer: "Several lifestyle inputs are pulling your hormonal output below where it should be.",
  },
  "age-related-decline": {
    stronger:
      "Age-related Leydig-cell decline is compounding with other suppressors. The drop is real, but the rate is not destiny.",
    softer:
      "Your body has begun the natural age-related decline. The slope is steeper than it needs to be.",
  },
  overtraining: {
    stronger: "Training load is outpacing recovery. Your testosterone-to-cortisol ratio is upside down.",
    softer:
      "Training load is starting to outpace recovery, suppressing testosterone signaling.",
  },
  "endocrine-disruption": {
    stronger:
      "Chemical exposure from processed food and packaging is creating a constant background load on your endocrine system.",
    softer:
      "Background endocrine-disruptor exposure is adding low-grade pressure to your hormonal system.",
  },
  "catabolic-deficit": {
    stronger:
      "Rapid weight loss is putting your body in crisis mode -- pulling testosterone down and breaking down muscle for fuel. The fat is leaving, but so is the strength underneath it.",
    softer:
      "Fast weight loss is starting to pull your testosterone down, so some of what you're losing is muscle, not just fat.",
  },
  "blocked-testosterone": {
    stronger:
      "Your testosterone isn't missing -- it's being locked away before your body can use it. SHBG binds what you produce, and too much is converted to estrogen first.",
    softer:
      "Your body is still making testosterone, but more of it is being bound and converted before it reaches your tissues.",
  },
  "exogenous-suppression": {
    stronger:
      "Exogenous testosterone has signaled your body to stop making its own. Your natural production has gone dormant -- that's the dependency you feel. Dormant isn't dead: the pathway restarts when the outside signal eases and the right inputs return.",
    softer:
      "Adding testosterone from outside is quietly telling your body to ease off its own production. The natural pathway is still there -- it just needs the signal to switch back on.",
  },
};

// Runtime safety net for buildAssessment. EXPLANATION is statically typed as
// Record<RootCause, ...>, so the compiler catches a missing key today, but a
// defensive fallback ensures the AssessmentCard never renders an undefined
// string if a future RootCause lands in the schema before its copy does.
const FALLBACK_EXPLANATION: Record<"stronger" | "softer", string> = {
  stronger: "Your responses point to a hormonal pattern that needs attention.",
  softer: "Your responses suggest your testosterone signaling is starting to drift.",
};

function buildAssessment(
  level: SuppressionLevel,
  primaryCause: RootCause,
): TSuppressionProfile["assessment"] {
  // No "low" / STABLE branch: the deriver floors at "normal" so STABLE is
  // unreachable (see baseSeverity + the floor-lock property test in
  // tests/quizzes/suppression-floor.test.ts). The ASSESSMENT_LABEL.low /
  // ASSESSMENT_HEADLINE.low entries remain only to satisfy the enum-keyed
  // Record type.
  const tone = level === "high" ? "stronger" : "softer";
  const explanation = EXPLANATION[primaryCause]?.[tone] ?? FALLBACK_EXPLANATION[tone];
  return {
    label: ASSESSMENT_LABEL[level],
    headline: ASSESSMENT_HEADLINE[level],
    explanation,
  };
}

// -- Evidence chips ------------------------------------------------------

const AGE_LABEL: Record<NonNullable<IgniteAnswer<"age">>, string> = {
  "50-plus": "50+",
  "40-49": "40–49",
  "30-39": "30–39",
  "20-29": "20–29",
};

const SLEEP_LABEL: Record<NonNullable<IgniteAnswer<"sleep-hours">>, string> = {
  "lt-5": "<5h / night",
  "5-6": "5–6h / night",
  "7-8": "7–8h / night",
  "gt-8": "8+h / night",
};

const ENERGY_LABEL: Record<NonNullable<IgniteAnswer<"energy-3pm">>, string> = {
  drained: "Drained",
  "afternoon-slump": "Afternoon slump",
  "slightly-tired": "Slightly tired",
  "same-energy": "Stable",
};

const BODY_LABEL: Record<NonNullable<IgniteAnswer<"body-composition">>, string> = {
  "lean-muscular": "Lean / muscular",
  average: "Average",
  "soft-some-muscle": "Soft / some muscle",
  overweight: "Overweight",
};

const ONSET_LABEL: Record<NonNullable<IgniteAnswer<"when-started">>, string> = {
  "over-year": "1+ years ago",
  "past-year": "Past year",
  "few-months": "Past few months",
  "not-sure": "Onset unclear",
};

const CONCERN_LABEL: Record<NonNullable<IgniteAnswer<"concern">>, string> = {
  energy: "Energy",
  muscle: "Muscle",
  "sex-drive": "Sex drive",
  focus: "Focus",
  "weight-loss": "Weight loss",
};

function deriveEvidence(answers: Answers): EvidenceItem[] {
  const out: EvidenceItem[] = [];

  const age = read(answers, "age");
  if (age) out.push({ label: "Age", value: AGE_LABEL[age] });

  const sleep = read(answers, "sleep-hours");
  if (sleep) out.push({ label: "Sleep", value: SLEEP_LABEL[sleep] });

  const stress = read(answers, "stress-symptoms");
  if (stress) {
    out.push({ label: "Stress symptoms", value: stress === "yes" ? "Elevated" : "None" });
  }

  const energy3pm = read(answers, "energy-3pm");
  if (energy3pm) out.push({ label: "3pm energy", value: ENERGY_LABEL[energy3pm] });

  // Cap at 4 chips for layout. Beyond that, fall back to the next most
  // diagnostic signals if the four above weren't all answered.
  if (out.length < 4) {
    const body = read(answers, "body-composition");
    if (body) out.push({ label: "Body comp", value: BODY_LABEL[body] });
  }
  if (out.length < 4) {
    const onset = read(answers, "when-started");
    if (onset) out.push({ label: "Onset", value: ONSET_LABEL[onset] });
  }
  if (out.length < 4) {
    const concern = read(answers, "concern");
    if (concern) out.push({ label: "Top concern", value: CONCERN_LABEL[concern] });
  }

  return out.slice(0, 4);
}

// -- Expected outcome (results-want -> personalized goal block) ----------

const EXPECTED_OUTCOMES: Record<NonNullable<IgniteAnswer<"results-want">>, ExpectedOutcome> = {
  energy: {
    label: "Sustained Energy",
    copy: "You want energy that doesn't crash at 3pm. Your protocol focuses there.",
  },
  muscle: {
    label: "Lean Mass & Strength",
    copy: "You want muscle that holds. Your protocol prioritizes anabolic signaling.",
  },
  "sex-drive": {
    label: "Drive & Performance",
    copy: "You want libido and performance back. Your protocol targets free-T circulation.",
  },
  focus: {
    label: "Mental Clarity",
    copy: "You want focus that holds. Your protocol restores the upstream hormonal signal.",
  },
  "all-of-above": {
    label: "Full Reset",
    copy: "You want all of it back. Your protocol works the three pathways at once.",
  },
};

const FALLBACK_EXPECTED_OUTCOME: ExpectedOutcome = {
  label: "Hormonal Recovery",
  copy: "Your protocol rebuilds the inputs your body needs to make its own testosterone.",
};

function deriveExpectedOutcome(answers: Answers): ExpectedOutcome {
  const want = read(answers, "results-want");
  if (want && want in EXPECTED_OUTCOMES) {
    return EXPECTED_OUTCOMES[want];
  }
  return FALLBACK_EXPECTED_OUTCOME;
}

// -- Public --------------------------------------------------------------

export function deriveIgniteProfile(
  answers: Answers,
  opts?: {
    extraSignals?: ExtraSignals;
    /**
     * Root-cause label override for wrapping derivers whose quiz's entire
     * entry promise names the cause (the cortisol quiz: every ad and bridge
     * says "cortisol", so the verdict must lead with it even on paths where
     * another cause out-scores it). Moves the forced cause to rootCauses[0]: the organic top scorer becomes
     * Secondary and any lower organic cause is dropped (the result is always
     * exactly 2 causes, never a 3-card grid). The assessment is built from the
     * forced primary, so the assessment card and the 2x2 grid can never desync.
     * Label-and-copy only: severity, decline pattern, QoL, and evidence still
     * derive from the real answers. Same seam contract as `extraSignals` —
     * opt-in, and existing callers (ignite, quiz-v7) are byte-identical when
     * it is omitted (locked by the regression snapshots).
     */
    forcePrimaryCause?: RootCause;
  },
): TSuppressionProfile {
  const suppressionLevel = deriveSuppressionLevel(answers, opts?.extraSignals);
  let rootCauses = pickRootCauses(answers);
  const forced = opts?.forcePrimaryCause;
  if (forced && rootCauses[0] !== forced) {
    rootCauses = [forced, ...rootCauses.filter((c) => c !== forced)].slice(0, 2);
  }
  const declinePattern = deriveDeclinePattern(answers);
  const qualityOfLifeImpact = deriveQolImpact(answers);
  const assessment = buildAssessment(suppressionLevel, rootCauses[0]!);
  const evidence = deriveEvidence(answers);
  const expectedOutcome = deriveExpectedOutcome(answers);

  return {
    suppressionLevel,
    assessment,
    declinePattern,
    rootCauses,
    qualityOfLifeImpact,
    evidence,
    expectedOutcome,
  };
}
